import React from 'react';
import { useNavigate } from 'react-router-dom';
import { FaTrashAlt, FaShoppingCart, FaLock, FaLeaf, FaSeedling } from 'react-icons/fa';

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,600;0,700;1,400;1,600&family=Lato:wght@300;400;700;900&display=swap');

  :root {
    --russian-red:  #CD0302;
    --sunburst:     #F0C400;
    --edam:         #F5D19D;
    --citrusy:      #F88702;
    --green:        #4F7E17;
    --green-light:  #6aad1f;
    --edam-light:   #fdf3e3;
    --edam-dark:    #e8c070;
    --soil:         #3d2b1f;
    --cream:        #fffdf5;
    --fog:          #f7f2ea;
  }

  .cp-root {
    font-family: 'Lato', sans-serif;
    min-height: 100vh;
    background:
      radial-gradient(ellipse at 0% 0%,    rgba(79,126,23,0.09)  0%, transparent 50%),
      radial-gradient(ellipse at 100% 100%, rgba(240,196,0,0.08)  0%, transparent 50%),
      var(--cream);
    padding: 36px 0 72px;
    color: var(--soil);
  }

  .cp-inner {
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 20px;
  }

  /* ── Page Header ── */
  .cp-header {
    margin-bottom: 32px;
    padding-bottom: 22px;
    border-bottom: 2px dashed var(--edam-dark);
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: 12px;
    flex-wrap: wrap;
  }

  .cp-header-left { display: flex; align-items: center; gap: 14px; }

  .cp-header-icon {
    width: 48px; height: 48px;
    background: linear-gradient(135deg, var(--green), var(--green-light));
    border-radius: 14px;
    display: flex; align-items: center; justify-content: center;
    box-shadow: 0 4px 14px rgba(79,126,23,0.28);
    flex-shrink: 0;
  }

  .cp-title {
    font-family: 'Playfair Display', Georgia, serif;
    font-size: clamp(1.6rem, 4vw, 2.2rem);
    font-weight: 700;
    font-style: italic;
    color: var(--soil);
    line-height: 1.1;
  }

  .cp-title span { color: var(--green); }

  .cp-count {
    font-size: 0.85rem;
    color: #9a8878;
    font-weight: 700;
    margin-top: 3px;
    letter-spacing: 0.03em;
  }

  /* ── Grid ── */
  .cp-grid {
    display: grid;
    grid-template-columns: 1fr;
    gap: 24px;
  }

  @media (min-width: 1024px) {
    .cp-grid { grid-template-columns: 1fr 360px; }
  }

  .cp-items-stack { display: flex; flex-direction: column; gap: 16px; }

  /* ── Item Card ── */
  .cp-item-card {
    background: #fff;
    border: 1.5px solid var(--edam);
    border-radius: 18px;
    padding: 22px;
    box-shadow: 0 2px 14px rgba(79,126,23,0.07);
    transition: box-shadow 0.25s, transform 0.2s, border-color 0.2s;
    position: relative;
    overflow: hidden;
  }

  .cp-item-card::before {
    content: '';
    position: absolute;
    top: 0; left: 0; right: 0;
    height: 3px;
    background: linear-gradient(90deg, var(--green), var(--citrusy), var(--sunburst));
    border-radius: 18px 18px 0 0;
    opacity: 0;
    transition: opacity 0.2s;
  }

  .cp-item-card:hover {
    box-shadow: 0 6px 24px rgba(79,126,23,0.13);
    border-color: var(--edam-dark);
    transform: translateY(-2px);
  }

  .cp-item-card:hover::before { opacity: 1; }

  .cp-item-inner {
    display: flex;
    flex-direction: column;
    gap: 18px;
  }

  @media (min-width: 560px) {
    .cp-item-inner { flex-direction: row; }
  }

  /* ── Product Image ── */
  .cp-item-img-wrap { flex-shrink: 0; }

  .cp-item-img {
    width: 100%;
    height: 160px;
    object-fit: cover;
    border-radius: 12px;
    border: 1.5px solid var(--edam);
    display: block;
  }

  @media (min-width: 560px) {
    .cp-item-img { width: 148px; }
  }

  /* ── Product Details ── */
  .cp-item-details { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 10px; }

  .cp-item-top {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 10px;
  }

  .cp-item-name {
    font-family: 'Playfair Display', serif;
    font-size: 1.05rem;
    font-weight: 700;
    color: var(--soil);
    line-height: 1.35;
  }

  .cp-remove-btn {
    flex-shrink: 0;
    width: 36px; height: 36px;
    border-radius: 10px;
    background: rgba(205,3,2,0.06);
    border: 1.5px solid rgba(205,3,2,0.18);
    color: var(--russian-red);
    display: flex; align-items: center; justify-content: center;
    cursor: pointer;
    transition: background 0.2s, transform 0.15s, box-shadow 0.2s;
  }

  .cp-remove-btn:hover {
    background: rgba(205,3,2,0.12);
    transform: scale(1.08);
    box-shadow: 0 3px 10px rgba(205,3,2,0.2);
  }

  .cp-price {
    font-family: 'Playfair Display', serif;
    font-size: 1.4rem;
    font-weight: 700;
    color: var(--russian-red);
  }

  .cp-badges {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
  }

  .cp-badge-stock {
    font-size: 0.72rem;
    font-weight: 900;
    letter-spacing: 0.07em;
    text-transform: uppercase;
    padding: 3px 10px;
    border-radius: 20px;
    background: rgba(79,126,23,0.12);
    color: var(--green);
    border: 1px solid rgba(79,126,23,0.2);
  }

  .cp-badge-ship {
    font-size: 0.78rem;
    color: #9a8878;
    font-weight: 600;
  }

  /* ── Size Select ── */
  .cp-option-row {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
  }

  .cp-option-label {
    font-size: 0.8rem;
    font-weight: 700;
    color: var(--soil);
    letter-spacing: 0.03em;
    flex-shrink: 0;
  }

  .cp-select-wrap { position: relative; }

  .cp-select-wrap::after {
    content: '▾';
    position: absolute;
    right: 10px; top: 50%;
    transform: translateY(-50%);
    color: var(--green);
    font-size: 0.75rem;
    pointer-events: none;
  }

  .cp-select {
    padding: 7px 28px 7px 12px;
    border: 1.5px solid var(--edam-dark);
    border-radius: 8px;
    background: var(--fog);
    font-family: 'Lato', sans-serif;
    font-size: 0.88rem;
    color: var(--soil);
    outline: none;
    appearance: none;
    -webkit-appearance: none;
    cursor: pointer;
    transition: border-color 0.2s, box-shadow 0.2s;
  }

  .cp-select:focus {
    border-color: var(--green);
    box-shadow: 0 0 0 3px rgba(79,126,23,0.12);
  }

  /* ── Color Dots ── */
  .cp-color-dot {
    width: 30px; height: 30px;
    border-radius: 50%;
    border: 2px solid var(--edam-dark);
    cursor: pointer;
    transition: transform 0.15s, border-color 0.15s, box-shadow 0.15s;
  }

  .cp-color-dot:hover { transform: scale(1.12); }

  .cp-color-dot.selected {
    border-color: var(--green);
    box-shadow: 0 0 0 3px rgba(79,126,23,0.2);
    transform: scale(1.08);
  }

  /* ── Qty Control ── */
  .cp-qty-wrap {
    display: flex;
    align-items: center;
    gap: 0;
    background: var(--fog);
    border: 1.5px solid var(--edam-dark);
    border-radius: 10px;
    overflow: hidden;
  }

  .cp-qty-btn {
    padding: 8px 14px;
    background: none;
    border: none;
    font-size: 1.1rem;
    font-weight: 700;
    color: var(--green);
    cursor: pointer;
    transition: background 0.15s;
  }

  .cp-qty-btn:hover:not(:disabled) { background: var(--edam); }

  .cp-qty-btn:disabled { opacity: 0.3; cursor: not-allowed; }

  .cp-qty-input {
    width: 52px;
    text-align: center;
    border: none;
    border-left: 1px solid var(--edam-dark);
    border-right: 1px solid var(--edam-dark);
    padding: 8px 4px;
    font-family: 'Lato', sans-serif;
    font-size: 0.92rem;
    font-weight: 700;
    color: var(--soil);
    background: #fff;
    outline: none;
  }

  .cp-qty-input:focus { box-shadow: inset 0 0 0 2px rgba(79,126,23,0.18); }

  /* Hide number input arrows */
  .cp-qty-input::-webkit-outer-spin-button,
  .cp-qty-input::-webkit-inner-spin-button { -webkit-appearance: none; }
  .cp-qty-input[type=number] { -moz-appearance: textfield; }

  /* ── Empty State ── */
  .cp-empty {
    background: #fff;
    border: 2px dashed var(--edam-dark);
    border-radius: 20px;
    padding: 72px 24px;
    text-align: center;
  }

  .cp-empty-icon {
    color: var(--edam-dark);
    font-size: 4rem;
    margin-bottom: 16px;
  }

  .cp-empty-title {
    font-family: 'Playfair Display', serif;
    font-size: 1.5rem;
    font-weight: 700;
    font-style: italic;
    color: var(--soil);
    margin-bottom: 8px;
  }

  .cp-empty-sub {
    font-size: 0.92rem;
    color: #9a8878;
    margin-bottom: 28px;
  }

  .cp-btn-shop {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 14px 32px;
    background: linear-gradient(135deg, var(--green), var(--green-light));
    color: #fff;
    border: none;
    border-radius: 14px;
    font-family: 'Playfair Display', serif;
    font-size: 1.05rem;
    font-weight: 700;
    cursor: pointer;
    transition: transform 0.2s, box-shadow 0.2s;
    box-shadow: 0 4px 16px rgba(79,126,23,0.32);
  }

  .cp-btn-shop:hover {
    transform: translateY(-2px);
    box-shadow: 0 7px 22px rgba(79,126,23,0.4);
  }

  /* ── Summary Card ── */
  .cp-summary {
    background: #fff;
    border: 1.5px solid var(--edam);
    border-radius: 20px;
    padding: 26px;
    position: sticky;
    top: 24px;
    box-shadow: 0 4px 24px rgba(79,126,23,0.09);
    overflow: hidden;
  }

  .cp-summary::before {
    content: '';
    position: absolute;
    top: 0; left: 0; right: 0;
    height: 4px;
    background: linear-gradient(90deg, var(--green), var(--citrusy), var(--sunburst));
    border-radius: 20px 20px 0 0;
  }

  .cp-summary-title {
    font-family: 'Playfair Display', serif;
    font-size: 1.25rem;
    font-weight: 700;
    font-style: italic;
    color: var(--soil);
    margin-bottom: 20px;
    padding-bottom: 16px;
    border-bottom: 1px dashed var(--edam-dark);
  }

  .cp-summary-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 0.92rem;
    margin-bottom: 10px;
  }

  .cp-summary-label { color: #7a6a58; font-weight: 600; }
  .cp-summary-val   { font-weight: 700; color: var(--soil); }

  .cp-summary-total-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 14px;
    padding-top: 14px;
    border-top: 2px solid var(--edam-dark);
  }

  .cp-summary-total-label {
    font-family: 'Playfair Display', serif;
    font-size: 1.08rem;
    font-weight: 700;
    color: var(--soil);
  }

  .cp-summary-total-val {
    font-family: 'Playfair Display', serif;
    font-size: 1.4rem;
    font-weight: 700;
    color: var(--russian-red);
  }

  /* ── Checkout Button ── */
  .cp-btn-checkout {
    width: 100%;
    margin-top: 20px;
    padding: 15px 20px;
    background: linear-gradient(135deg, var(--green) 0%, var(--green-light) 100%);
    color: #fff;
    border: none;
    border-radius: 14px;
    font-family: 'Playfair Display', serif;
    font-size: 1.05rem;
    font-weight: 700;
    cursor: pointer;
    transition: transform 0.2s, box-shadow 0.2s, filter 0.2s;
    box-shadow: 0 4px 18px rgba(79,126,23,0.32);
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
  }

  .cp-btn-checkout:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 8px 26px rgba(79,126,23,0.4);
    filter: brightness(1.05);
  }

  .cp-btn-checkout:disabled {
    background: linear-gradient(135deg, #c8c8c8, #b0b0b0);
    color: #777;
    cursor: not-allowed;
    box-shadow: none;
    transform: none;
  }

  /* ── Trust Badges ── */
  .cp-trust-list {
    margin-top: 20px;
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  .cp-trust-item {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    font-size: 0.82rem;
    color: #7a6a58;
    line-height: 1.45;
  }

  .cp-trust-icon {
    flex-shrink: 0;
    width: 20px; height: 20px;
    border-radius: 50%;
    background: rgba(79,126,23,0.12);
    display: flex; align-items: center; justify-content: center;
    color: var(--green);
    margin-top: 1px;
  }
`;

function CartPage({ cartItems, updateCartQuantity, removeFromCart, updateCartItemOptions }) {
  const navigate = useNavigate();

  const total = cartItems.reduce((acc, item) => acc + item.price * item.quantity, 0);

  const handleSizeChange  = (itemId, size)  => updateCartItemOptions(itemId, 'selectedSize', size);
  const handleColorChange = (itemId, color) => updateCartItemOptions(itemId, 'selectedColor', color);

  const handleProceedToCheckout = () => {
    navigate('/checkout', { state: { cartItems } });
  };

  return (
    <>
      <style>{styles}</style>
      <div className="cp-root">
        <div className="cp-inner">

          {/* ── Header ── */}
          <div className="cp-header">
            <div className="cp-header-left">
              <div className="cp-header-icon">
                <FaShoppingCart style={{ color: '#fff', fontSize: '1.3rem' }} />
              </div>
              <div>
                <h1 className="cp-title">Shopping <span>Cart</span></h1>
                <p className="cp-count">
                  🌿 {cartItems.length} {cartItems.length === 1 ? 'item' : 'items'} in your basket
                </p>
              </div>
            </div>
          </div>

          {/* ── Empty State ── */}
          {cartItems.length === 0 ? (
            <div className="cp-empty">
              <div className="cp-empty-icon">
                <FaShoppingCart />
              </div>
              <h2 className="cp-empty-title">Your basket is empty</h2>
              <p className="cp-empty-sub">Add some fresh products to get started!</p>
              <button className="cp-btn-shop" onClick={() => navigate('/')}>
                <FaSeedling /> Continue Shopping
              </button>
            </div>
          ) : (
            <div className="cp-grid">

              {/* ── Items ── */}
              <div className="cp-items-stack">
                {cartItems.map((item) => (
                  <div key={item.id} className="cp-item-card">
                    <div className="cp-item-inner">

                      {/* Image */}
                      <div className="cp-item-img-wrap">
                        <img src={item.image} alt={item.name} className="cp-item-img" />
                      </div>

                      {/* Details */}
                      <div className="cp-item-details">

                        {/* Name + Remove */}
                        <div className="cp-item-top">
                          <h3 className="cp-item-name">{item.name}</h3>
                          <button
                            className="cp-remove-btn"
                            onClick={() => removeFromCart(item.id)}
                            aria-label="Remove item"
                          >
                            <FaTrashAlt size={13} />
                          </button>
                        </div>

                        {/* Price */}
                        <div className="cp-price">৳{item.price.toLocaleString()}</div>

                        {/* Badges */}
                        <div className="cp-badges">
                          <span className="cp-badge-stock">✓ In Stock</span>
                          <span className="cp-badge-ship">🚚 FREE Shipping eligible</span>
                        </div>

                        {/* Size */}
                        {item.isSizeAvailable && item.sizes && item.sizes.length > 0 && (
                          <div className="cp-option-row">
                            <span className="cp-option-label">Size:</span>
                            <div className="cp-select-wrap">
                              <select
                                className="cp-select"
                                value={item.selectedSize || ''}
                                onChange={(e) => handleSizeChange(item.id, e.target.value)}
                              >
                                <option value="" disabled>Select size</option>
                                {item.sizes.map((size) => (
                                  <option key={size} value={size}>{size}</option>
                                ))}
                              </select>
                            </div>
                          </div>
                        )}

                        {/* Color */}
                        {item.isColorAvailable && item.colors && item.colors.length > 0 && (
                          <div className="cp-option-row">
                            <span className="cp-option-label">Color:</span>
                            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                              {item.colors.map((color) => (
                                <button
                                  key={color.name}
                                  className={`cp-color-dot ${item.selectedColor?.name === color.name ? 'selected' : ''}`}
                                  style={{ backgroundColor: color.hex }}
                                  onClick={() => handleColorChange(item.id, color)}
                                  aria-label={color.name}
                                  title={color.name}
                                />
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Quantity */}
                        <div className="cp-option-row">
                          <span className="cp-option-label">Qty:</span>
                          <div className="cp-qty-wrap">
                            <button
                              className="cp-qty-btn"
                              onClick={() => updateCartQuantity(item.id, item.quantity - 1)}
                              disabled={item.quantity <= 1}
                            >−</button>
                            <input
                              type="number"
                              className="cp-qty-input"
                              value={item.quantity}
                              onChange={(e) => updateCartQuantity(item.id, parseInt(e.target.value) || 1)}
                              min="1"
                            />
                            <button
                              className="cp-qty-btn"
                              onClick={() => updateCartQuantity(item.id, item.quantity + 1)}
                            >+</button>
                          </div>
                        </div>

                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* ── Summary ── */}
              <div>
                <div className="cp-summary">
                  <h2 className="cp-summary-title">Order Summary</h2>

                  <div className="cp-summary-row">
                    <span className="cp-summary-label">Subtotal ({cartItems.length} items)</span>
                    <span className="cp-summary-val">৳{total.toLocaleString()}</span>
                  </div>

                  <div className="cp-summary-total-row">
                    <span className="cp-summary-total-label">Order Total</span>
                    <span className="cp-summary-total-val">৳{total.toLocaleString()}</span>
                  </div>

                  <button
                    className="cp-btn-checkout"
                    onClick={handleProceedToCheckout}
                    disabled={cartItems.length === 0}
                  >
                    <FaLock size={13} />
                    Proceed to Checkout
                  </button>

                  {/* Trust Badges */}
                  <div className="cp-trust-list">
                    {[
                      'Free delivery on orders over ৳500',
                      'Secure checkout with SSL encryption',
                      'Easy returns within 30 days',
                    ].map((text, i) => (
                      <div key={i} className="cp-trust-item">
                        <div className="cp-trust-icon">
                          <svg width="11" height="11" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                        </div>
                        <span>{text}</span>
                      </div>
                    ))}
                  </div>

                  {/* Organic strip */}
                  <div style={{
                    marginTop: '20px',
                    padding: '12px 14px',
                    background: 'rgba(79,126,23,0.06)',
                    border: '1px dashed rgba(79,126,23,0.25)',
                    borderRadius: '10px',
                    textAlign: 'center',
                    fontSize: '0.75rem',
                    color: '#7a6a58',
                    lineHeight: 1.6,
                  }}>
                    🌿 100% Organic &nbsp;·&nbsp; 🔒 Secure &nbsp;·&nbsp; 🚚 Fast Delivery
                  </div>
                </div>
              </div>

            </div>
          )}
        </div>
      </div>
    </>
  );
}

export default CartPage;